import React, { useLayoutEffect, useRef, useState } from "react";
import { X } from "react-feather";

import { floatToCurrency } from "../../constants/validate";
import { useSelector } from "react-redux";
import { useSnackbar } from "../../hooks/useSnackbar";
import axios from "axios";

import { Accordion } from "../Accordion";
import Spinner from "../Spinner";
import Button from "../DesignSystem/Button";

import "./index.scss";

function DebtInformation({
  title,
  topics,
  hasStatus = false,
  statusProposal,
  accordions,
  classSpecial,
  isLoading = true,
  handleCallbackAccordion,
  hasCloseIcon,
  handleClose,
}) {
  const containerSubtopics = useRef();
  const setSnackbar = useSnackbar();
  const [sendingReminder, setSendingReminder] = useState(false);

  const { currentCase } = useSelector((state) => state.crm);

  const caseData = currentCase?.case || currentCase;

  const observer = useRef(
    new ResizeObserver(() => {
      handleDefineWidthSubtopics();
    })
  );

  function handleDefineWidthSubtopics() {
    if (containerSubtopics.current) {
      let widthFirstColumn = `${containerSubtopics.current.firstChild.firstChild.getBoundingClientRect()
        .width
        }px`;
      const children = Array.from(containerSubtopics.current.children);
      children.forEach((item, index) => {
        if (index > 0) {
          item.firstChild.style.width = widthFirstColumn;
        }
      });
    }
  }

  useLayoutEffect(() => {
    const currentObserver = observer.current;
    const subtopicsContainerCurrent = containerSubtopics.current;
    if (subtopicsContainerCurrent) {
      currentObserver.observe(subtopicsContainerCurrent);
    }

    return () => {
      currentObserver.disconnect();
    };
  }, [observer, isLoading]);

  function renderIcon(Icon) {
    return <Icon size={20} />;
  }

  function returnClassNameIndicator() {
    let className = "debt-info__indicator";

    if (statusProposal === "SENT" || statusProposal === "NEGOTIATION")
      className += " debt-info__indicator--active";
    if (statusProposal === "WAITING_APPROVAL")
      className += " debt-info__indicator--waiting-approval";
    if (statusProposal === "WAITING_PAYMENT")
      className += " debt-info__indicator--waiting-payment";
    if (statusProposal === "WAITING")
      className += " debt-info__indicator--waiting";
    if (statusProposal === "PAID") className += " debt-info__indicator--paid";
    if (statusProposal === "ARCHIVED" || statusProposal === "CLOSED")
      className += " debt-info__indicator--archived";

    return className;
  }

  function returnStatusLabel(status) {
    switch (status) {
      case "SENT":
        return "Ativa";
      case "NEGOTIATION":
        return "Em Negociação";
      case "WAITING_APPROVAL":
        return "Esperando assinaturas";
      case "PAID":
        return "Concluído";
      case "ARCHIVED":
        return "Arquivada";
      case "WAITING":
        return "Aguardando assinatura e pagamento";
      case 'CLOSED':
        return "Encerrado";
      case 'CHAT NEGOTIATION':
          return "Em negociação por chat";
      default:
        return "Aguardando pagamento";
    }
  }

  function returnClassNameSubtopics(tableFormat = false) {
    let className = "debt-info__subtopics";

    if (tableFormat) className += " debt-info__subtopics--table";

    return className;
  }

  function copyToClipboard(text) {
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setSnackbar("Link copiado!", { time: 3000 });
      });
    } else {
      const input = document.createElement("input");
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setSnackbar("Link copiado!", { time: 3000 });
    }
  }

  async function handleSendReminder() {
    if (!currentCase?.case?.case_id) return;
    setSendingReminder(true);
    try {
      await axios.post(`/case/send-reminder/${currentCase.case.case_id}`);
      setSnackbar("Lembrete enviado com sucesso!", { type: "success" });
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "Erro ao enviar lembrete.";
      setSnackbar(msg, { type: "error" });
    } finally {
      setSendingReminder(false);
    }
  }

  return (
    <section className={`debt-info ${classSpecial}`}>
      {hasCloseIcon && <X className="debt-info__close" onClick={handleClose} />}
      <header className="debt-info__header">
        <h2 className="debt-info__title">{title}</h2>
        {hasStatus && !isLoading && caseData?.cases_types_id && (
          <div className="debt-info__status">
            <span>Caso</span>
            <div className={returnClassNameIndicator()} />
            <strong>{returnStatusLabel(statusProposal)}</strong>
          </div>
        )}
        {!isLoading && caseData?.case_id && (
          <Button
            size="small"
            outlined
            contained
            fullWidth
            textUppercase
            onClick={handleSendReminder}
            loading={sendingReminder}
          >
            Lembrete Assinatura
          </Button>
        )}
      </header>
      {isLoading ? (
        <Spinner size={24} containerHeight="100%" />
      ) : (
        <main>
          <div className="debt-info__topics">
            {topics.map((topic, index) => {
              if (topic.hasSubtopics) {
                return (
                  <div className="debt-info__topic" key={index}>
                    <div className="debt-info__topics__title">
                      {topic.type === "link" ? (
                        <topic.Icon
                          size={20}
                          className="debt-info__icon-link"
                          onClick={() => copyToClipboard(topic.value)}
                          title="Copiar link"
                        />
                      ) : (
                        renderIcon(topic.Icon)
                      )}
                      <span>{topic.title}</span>
                    </div>
                    <div
                      ref={containerSubtopics}
                      className={returnClassNameSubtopics(topic.tableFormat)}
                    >
                      {topic.subtopics.map((subtopic, subtopicIndex) => {
                        return (
                          <div key={subtopicIndex}>
                            <p title={subtopic.description}>
                              {subtopic.description}
                            </p>
                            <span>
                              {subtopic.type === "currency"
                                ? floatToCurrency(Number(subtopic.value))
                                : subtopic.value}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="debt-info__topic" key={topic.title}>
                    <div className="debt-info__topics__title">
                      {topic.type === "link" ? (
                        <topic.Icon
                          size={20}
                          className="debt-info__icon-link"
                          onClick={() => copyToClipboard(topic.value)}
                          title="Copiar link"
                        />
                      ) : (
                        renderIcon(topic.Icon)
                      )}
                      <span>{topic.title}</span>
                    </div>
                    {topic.type === "link" ? (
                      <a
                        className="debt-info__link"
                        href={topic.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={topic.value}
                      >
                        {topic.value}
                      </a>
                    ) : (
                      <p>{topic.value}</p>
                    )}
                  </div>
                );
              }
            })}
          </div>
          {accordions && (
            <div className="debt-info__container-accordions">
              {accordions.map((accordion) => (
                <div
                  className="debt-info__wrapper-accordion"
                  key={accordion.key}
                >
                  <Accordion
                    title={accordion.title}
                    icon={accordion.icon}
                    wide
                    handleCallbackAccordion={handleCallbackAccordion}
                  >
                    {accordion.component}
                  </Accordion>
                </div>
              ))}
            </div>
          )}
        </main>
      )}
    </section>
  );
}

export default DebtInformation;
